import { useCallback, useMemo, useRef, useState, useTransition } from 'react';
import type { Selection } from 'react-aria-components';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableViewCreationFromSelect, enableViewFavoriting, enableViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackButton } from '@gonfalon/feedback/ContextualFeedbackButton';
import type { UIFlagFilter } from '@gonfalon/flag-filters/types';
import { SearchField } from '@gonfalon/launchpad-experimental/Form';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { AccessCheck } from '@gonfalon/permissions/access-check';
import { getProjectQuery } from '@gonfalon/rest-api/queries/projects';
import { getViewsQuery } from '@gonfalon/rest-api/queries/views';
import { Box } from '@launchpad-ui/box';
import {
  Button,
  ButtonGroup,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  ProgressBar,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import type { ViewSelectedResource } from '../components/AddResourcesToViewModal';
import { VIEWS_QUERY_STALE_TIME } from '../constants';
import type { LinkableResourceType, View } from '../types';
import { useViewCreation } from '../ViewCreationContext';

import { ProjectEmptyViewsState } from './ProjectEmptyViewsState';
import { RichViewsDialog } from './RichViewsDialog';

import styles from './ViewsDialog.module.css';

export type ViewsDialogProps = {
  onSelectionChange: (views: View[]) => void;
  selectedViews: View[];
  label?: string;
  selectionMode?: 'single' | 'multiple';
  allowClear?: boolean;
  onBeforeCreateView?: () => void;
  onFeedbackPress?: () => void;
  onExpandDetails?: () => void;
  flagFilters?: UIFlagFilter;
  resourceType?: LinkableResourceType;
  initialSelectedResources?: Array<ViewSelectedResource<LinkableResourceType>>;
  /**
   * When true, suppress the footer `Create view` affordance. Useful in filter
   * contexts where inline view creation doesn't make sense (e.g. the SDK Keys
   * view filter — see payload-filtering-v2 spec §15.4).
   */
  hideCreateButton?: boolean;
  /**
   * Project key to scope the views query against. When omitted, falls back to the active
   * `useProjectContext()`. Required when this dialog is mounted outside of the project-context
   * route loader (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`).
   */
  projectKey?: string;
};

export function ViewsDialog(props: ViewsDialogProps) {
  if (enableViewFavoriting()) {
    const {
      onSelectionChange,
      selectedViews = [],
      label = 'Select views',
      selectionMode = 'multiple',
      onBeforeCreateView,
      flagFilters,
      resourceType,
      initialSelectedResources,
      hideCreateButton,
      projectKey,
    } = props;

    return (
      <Dialog aria-label={label} className={styles.compactRichDialog}>
        <RichViewsDialog
          onSelectionChange={onSelectionChange}
          selectedViews={selectedViews}
          label={label}
          selectionMode={selectionMode}
          onBeforeCreateView={onBeforeCreateView}
          flagFilters={flagFilters}
          resourceType={resourceType}
          initialSelectedResources={initialSelectedResources}
          isExpanded={false}
          hideSelectionFooter
          hideCreateButton={hideCreateButton}
          projectKey={projectKey}
        />
      </Dialog>
    );
  }

  return <LegacyViewsDialog {...props} />;
}

function LegacyViewsDialog({ projectKey: projectKeyProp, ...props }: ViewsDialogProps) {
  // Same fallback rationale as `RichViewsDialog` — see its top-of-function comment for context.
  const projectContext = useProjectContext({ optional: true });
  const projectKey = projectKeyProp ?? projectContext?.project.key;
  // Resolve `projectKey` and guard here — before any other hooks run — so the conditional throw can
  // never change the hook count across renders. The hook-heavy body lives in `LegacyViewsDialogContent`.
  if (!projectKey) {
    throw new Error(
      'ViewsDialog requires a `projectKey` prop when rendered outside of the project-context route loader.',
    );
  }
  // Only forward the context project when the caller didn't override `projectKey` — see
  // `RichViewsDialog` for the rationale (avoids handing a mismatched project to `AccessCheck`).
  return (
    <LegacyViewsDialogContent
      {...props}
      projectKey={projectKey}
      contextProject={projectKeyProp ? undefined : projectContext?.project}
    />
  );
}

type LegacyViewsDialogContentProps = Omit<ViewsDialogProps, 'projectKey'> & {
  projectKey: string;
  contextProject?: NonNullable<ReturnType<typeof useProjectContext>>['project'];
};

function LegacyViewsDialogContent({
  onSelectionChange,
  selectedViews = [],
  label = 'Select views',
  selectionMode = 'multiple',
  allowClear,
  onBeforeCreateView,
  onFeedbackPress,
  onExpandDetails,
  flagFilters,
  resourceType,
  initialSelectedResources,
  hideCreateButton = false,
  projectKey,
  contextProject,
}: LegacyViewsDialogContentProps) {
  const [filterText, setFilterText] = useState('');
  const [isTransitioning, startTransition] = useTransition();
  // `project` is required by downstream `AccessCheck` wrappers. Inside the project-context loader
  // we already have it from `contextProject`; outside (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`)
  // we suspense-fetch from the resolved `projectKey`. The query is cached by React Query, so when
  // the project context already populated it (in-loader path) this is effectively free.
  const { data: fetchedProject } = useSuspenseQuery(getProjectQuery({ projectKey }));
  const project = contextProject ?? fetchedProject;

  const { openCreateModal, setOnViewCreated } = useViewCreation();
  const isCreateViewFeatureEnabled = enableViews() && enableViewCreationFromSelect() && !hideCreateButton;
  const canRenderFeedback = Boolean(onFeedbackPress);
  // `shouldRenderFooterCreate` is recomputed later once we know if the project
  // is empty (to avoid duplicate CTA with `ProjectEmptyViewsState`).

  // Use refs to avoid stale closures in the callback passed to setOnViewCreated
  const selectedViewsRef = useRef(selectedViews);
  const onSelectionChangeRef = useRef(onSelectionChange);
  selectedViewsRef.current = selectedViews;
  onSelectionChangeRef.current = onSelectionChange;

  const handleCreateClick = useCallback(() => {
    onBeforeCreateView?.();
    setOnViewCreated((view) => {
      onSelectionChangeRef.current([...selectedViewsRef.current, view]);
    });
    openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources);
  }, [
    filterText,
    flagFilters,
    resourceType,
    initialSelectedResources,
    onBeforeCreateView,
    openCreateModal,
    setOnViewCreated,
  ]);

  const { data: viewsRes, isPending } = useSuspenseQuery({
    ...getViewsQuery({
      projectKey,
      query: filterText ? { filter: { query: filterText } } : undefined,
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEWS_QUERY_STALE_TIME,
  });

  const allViews = useMemo(
    () => uniqBy([...selectedViews, ...(viewsRes?.items ?? [])], 'key'),
    [viewsRes, selectedViews],
  );

  const isLoading = isTransitioning || isPending;

  const handleFilterChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  const handleSelectionChange = (selection: Selection) => {
    if (typeof selection === 'string') {
      return;
    }

    const keys = Array.from(selection);
    if (keys.length > 0) {
      const views = keys.map((key) => allViews.find((v) => v.key === key)).filter((v) => !!v);
      onSelectionChange(views);
      return;
    }

    // nothing is selected
    onSelectionChange([]);
  };

  const renderViewMenuItem = (view: View) => {
    return (
      <MenuItem key={view.key} textValue={view.name} className={styles.viewMenuItem}>
        <Text slot="label">
          <div className={styles.viewMenuItemLabel}>
            <Icon name="stack" size="small" className={styles.viewNameIcon} />
            <TextTruncator>{view.name}</TextTruncator>
          </div>
        </Text>
      </MenuItem>
    );
  };

  const selectedKeys = selectedViews.map((view) => view.key);
  const isProjectEmpty = !isLoading && filterText.trim() === '' && allViews.length === 0;
  // Suppress the footer Create CTA when `ProjectEmptyViewsState` renders — the
  // empty state already owns the primary CTA, so avoid a duplicate.
  const shouldRenderFooterCreate = isCreateViewFeatureEnabled && !isProjectEmpty;
  const canRenderFooter = shouldRenderFooterCreate || allowClear || canRenderFeedback || !!onExpandDetails;
  const showPrimaryFooterRow = shouldRenderFooterCreate || !!onExpandDetails;

  const emptyState = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <ProgressBar aria-label="Loading…" isIndeterminate size="small" />
        </Box>
      );
    }
    if (filterText.trim() !== '') {
      // Search no-match — keep existing behavior.
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <>No views found</>
        </Box>
      );
    }
    // Project has zero views — render the centered block with Create CTA.
    // Mirror the header/footer gating: suppress the empty-state Create button
    // whenever the create-view feature itself is disabled (feature flags off,
    // or caller passed `hideCreateButton`). Otherwise we'd render a Create CTA
    // here that the rest of the dialog intentionally hides.
    return <ProjectEmptyViewsState onCreateView={handleCreateClick} hideCreateButton={!isCreateViewFeatureEnabled} />;
  };

  return (
    <Dialog aria-label={label}>
      <SearchField
        autoFocus
        aria-label="Search views"
        isBusy={isTransitioning}
        onChange={handleFilterChange}
        placeholder="Search views"
      />
      <div className={styles.viewsList}>
        <Menu
          selectionMode={selectionMode}
          items={allViews}
          selectedKeys={selectedKeys}
          aria-label="Select views"
          onSelectionChange={handleSelectionChange}
          renderEmptyState={emptyState}
        >
          {renderViewMenuItem}
        </Menu>
      </div>

      {canRenderFooter && (
        <Box display="flex" className={styles.footer} gap="$200">
          {showPrimaryFooterRow && (
            <div className={styles.footerPrimaryRow}>
              {shouldRenderFooterCreate && (
                <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                  <div className={`${styles.createView} ${onExpandDetails ? styles.createViewWithSibling : ''}`.trim()}>
                    <Button variant="minimal" size="small" onPress={handleCreateClick}>
                      <Icon name="add" size="small" />
                      <span>{filterText.trim() ? `Create "${filterText.trim()}"` : 'Create view'}</span>
                    </Button>
                  </div>
                </AccessCheck>
              )}
              {onExpandDetails && (
                <TooltipTrigger>
                  <IconButton
                    aria-label="Expand details"
                    icon="chevrons-right"
                    size="small"
                    variant="minimal"
                    className={styles.expandDetailsButton}
                    onPress={onExpandDetails}
                  />
                  <Tooltip>Expand details</Tooltip>
                </TooltipTrigger>
              )}
            </div>
          )}
          {(allowClear || canRenderFeedback) && (
            <Box>
              <ButtonGroup spacing="basic">
                {allowClear && (
                  <Button size="small" onClick={() => onSelectionChange([])}>
                    Clear
                  </Button>
                )}
                {canRenderFeedback && <ContextualFeedbackButton size="small" onPress={onFeedbackPress} isCompact />}
              </ButtonGroup>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
}
